import {Record, List} from 'immutable';

import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import Table from 'web-app/react/components/table/model';
import {FRONTEND_UNKNOWN_ERROR_CODE} from 'web-app/react/plans/debt-report/batch-invoice/constants';
import {isNonEmpty} from 'web-app/util/non-empty-array';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import {type CreateInvoiceMutation} from './__generated__/queries.api-types';

const Constants = {
    NoCreditNote: 1,
    HasCreditNote: 2,
    IsCreditNote: 3,

    DRAFT: 'DRAFT' as const,
    Payment: 'payment' as const,
    Invoice: 'invoice' as const,
};

interface IPayer {
    name: string;
    address: string;
    email: string;
    billPayerId: string;
    relationId: string;
}

class Payer extends Record<IPayer>({
    name: '',
    address: '',
    email: '',
    billPayerId: '',
    relationId: '',
}) {}

interface ILinkedInvoice {
    status: number;
    id: string;
    title: string;
}

class LinkedInvoice extends Record<ILinkedInvoice>({
    status: 0,
    id: '',
    title: '',
}) {}

interface IVoucherCompany {
    voucherCompanyId: string;
    name: string;
}

class VoucherCompany extends Record<IVoucherCompany>({
    voucherCompanyId: '',
    name: '',
}) {}

interface IDirectDebit {
    accountName: string;
    accountNo: string;
    sortCode: string;
    billingReference: string;
    autoAddPayments: string;
    createdAt: string;
    lastChargedAt: string;
}

class DirectDebit extends Record<IDirectDebit>({
    accountName: '',
    accountNo: '',
    sortCode: '',
    billingReference: '',
    autoAddPayments: '',
    createdAt: '',
    lastChargedAt: '',
}) {}

interface IPaymentMethod {
    id: string;
    title: string;
}

class PaymentMethod extends Record<IPaymentMethod>({
    id: '',
    title: '',
}) {}

interface IGraphQLPdf {
    invoiceId: string;
    __typename: string;
    data: string;
}

class GraphQLPdf extends Record<IGraphQLPdf>({
    invoiceId: '',
    __typename: '',
    data: '',
}) {}

interface IAccountingItem {
    type: typeof Constants['DRAFT'] | typeof Constants['Invoice'] | typeof Constants['Payment'] | '';
    id: string;
    title: string;
    subtitle: string;
    billPayerId: string;
    institutionId: string;
    status: number;
    invoiceNo: number;
    date: string;
    due: string;
    amount: number | string;
    balance?: any;
    coveringMonths: List<any>;
    note: string;
    pdf: string | null;
    graphQLPdf: GraphQLPdf | null;
    directDebit: DirectDebit | null;
    payer: Payer;
    isDeposited: any;
    depositDate: string | null;
    depositId: string | null;
    paymentMethod: PaymentMethod | null;
    voucherCompany: VoucherCompany | null;
    recurrenceId: string | null;
    recurringPaymentId: string | null;
    confirmed: any;
    behaviors: BehaviorsCollection;
    invoiceIds: List<string>;
    linkedInvoices: List<any>;
    directDebitSelected: boolean;
    transactionStatus: any;
    famlyPay: boolean;
    createdBy: any;
    createdAt: any;
    deletedBy: any;
    deletedAt: any;
    userFee: any;
    details: any;
}

export class AccountItem extends Record<IAccountingItem>({
    type: '',
    id: '',
    title: '',
    subtitle: '',
    billPayerId: '',
    institutionId: '',
    status: 0,
    invoiceNo: 0,
    date: '',
    due: '',
    amount: 0,
    balance: undefined,
    coveringMonths: List(),
    note: '',
    pdf: '',
    graphQLPdf: null,
    directDebit: null,
    payer: new Payer(),
    isDeposited: null,
    depositDate: null,
    depositId: null,
    paymentMethod: null,
    voucherCompany: null,
    recurrenceId: null,
    recurringPaymentId: null,
    confirmed: null,
    behaviors: new BehaviorsCollection([]),
    invoiceIds: List(),
    linkedInvoices: List(),
    directDebitSelected: false,
    transactionStatus: null,
    famlyPay: false,
    createdBy: null,
    createdAt: null,
    deletedBy: null,
    deletedAt: null,
    userFee: null,
    details: null,
}) {}

function fromAPI(data: any) {
    return new AccountItem({
        ...data,
        payer: new Payer(data.payer),
        directDebit: data.directDebit ? new DirectDebit(data.directDebit) : null,
        paymentMethod: data.paymentMethod ? new PaymentMethod(data.paymentMethod) : null,
        voucherCompany: data.voucherCompany ? new VoucherCompany(data.voucherCompany) : null,
        behaviors: data.behaviors ? new BehaviorsCollection(data.behaviors) : new BehaviorsCollection([]),
        linkedInvoices: List(data.linkedInvoices).map((inv: any) => new LinkedInvoice(inv)),
        invoiceIds: data.invoiceIds ? List(data.invoiceIds) : List(),
        coveringMonths: data.coveringMonths ? List(data.coveringMonths) : List(),
    });
}

const fromGraphQLInvoice = (result: CreateInvoiceMutation['finance']['invoices']['create']['invoices'][0]) => {
    if (result.__typename === 'InvoiceFailure') {
        const errors = result.errors;
        if (isNonEmpty(errors)) {
            return {
                type: 'error' as const,
                billPayerId: result.billPayer.billPayerId,
                payer: result.billPayer,
                error: errors[0].message,
                errorCode: errors[0].code,
            };
        }

        // this should not happen, if InvoiceFailure is returned it should have an error in the array
        return {
            type: 'error' as const,
            billPayerId: result.billPayer.billPayerId,
            payer: result.billPayer,
            error: 'Unknown Error',
            errorCode: FRONTEND_UNKNOWN_ERROR_CODE,
        };
    }

    if (result.__typename !== 'InvoiceSuccess') {
        return null;
    }

    const children = result.invoice.invoiceDetailsCore && JSON.parse(result.invoice.invoiceDetailsCore).children;

    const details = children
        ? List(
              children.map(({child, tables}) => ({
                  child,
                  tables: List(tables).map(table => Table.fromAPI(table)),
              })),
          )
        : undefined;

    return new AccountItem({
        type: Constants.Invoice,
        ...result.invoice,
        pdf: null,
        graphQLPdf: hasValue(result.invoice.pdf) ? new GraphQLPdf(result.invoice.pdf) : null,
        id: result.invoice.invoiceId || Constants.DRAFT,
        payer: new Payer(result.invoice.billPayer),
        billPayerId: result.invoice.billPayer.billPayerId,
        amount: result.invoice.total,
        details,
    });
};

export default {
    record: AccountItem,
    default: new AccountItem(),
    Constants,
    fromAPI,
    fromGraphQLInvoice,
};
